<template>
    <div class="pane-content">
        <!-- Start HubSpot Workflow > Send a text (SMS/MMS) Warning -->
        <hubspot-settings-workflow-notice class="mt-2" v-if="integrationName === 'hubspot'" />
        <!-- End HubSpot Workflow > Send a text (SMS/MMS) Warning -->

        <!-- Start Status Row -->
        <el-row type="flex" class="mt-4 mb-1">
            <h5 class="status-label">Integration Status</h5>
            <el-switch v-model="model.status" @change="checkAndSave" active-color="#00a344"></el-switch>
        </el-row>
        <!-- End Status Row -->

        <!-- Start Re-Authenticate -->
        <el-row type="flex" class="mt-2"
                v-if="integrationName === SALESFORCE_INTEGRATION">
            <salesforce-settings-reauthenticate :oauth="oauth"
                                                :has-access-token="hasAccessToken"
                                                :other-crm-is-enabled="otherCrmIsEnabled"
                                                :integration-name="integrationName" />
        </el-row>
        <!-- End Re-Authenticate -->

        <!-- Start Connection Status -->
        <div class="d-flex align-items-center mt-2">
            <connection-status :connection-status="model.connection_status" />
        </div>
        <!-- End Connection Status -->

        <!-- Start Re-Authenticate & Test Connection Buttons -->
        <el-row type="flex" class="mt-2">
            <settings-authentication :oauth="oauth"
                                     :has-access-token="hasAccessToken"
                                     :other-crm-is-enabled="otherCrmIsEnabled"
                                     :integration-name-with-casing="integrationNameWithCasing"/>

            <!-- Start Connection Tester Button -->
            <connection-tester-btn
                    :integration-name="integrationName"
                    :integration-name-with-casing="integrationNameWithCasing"
                    :connection-status="model.connection_status"
                    @on-success="handleConnectionTest"
                    class="ml-2 btn-text"
            />
            <!-- End Connection Tester Button -->
        </el-row>
        <!-- End Re-Authenticate & Test Connection Buttons -->

        <!-- Start Connection Error -->
        <div v-if="showConnectionError" class="connection-error-container mt-1">
            <span class="connection-error-text">Error: {{ model.last_connection_error }}</span>
            <button
                    v-clipboard:copy="model.last_connection_error"
                    v-clipboard:success="onClipboardCopy"
                    v-clipboard:error="onClipboardError"
                    size="mini"
                    class="ml-2">
                <i class="el-icon-document-copy"></i>
            </button>
        </div>
        <!-- End Connection Error -->

        <!-- Start Connection Information -->
        <hubspot-connection-info-details
                :model="model"
                v-if="integrationName === HUBSPOT_INTEGRATION"/>

        <salesforce-connection-info-details
                :model="model"
                v-if="integrationName === SALESFORCE_INTEGRATION"/>

        <gohighlevel-connection-info-details
                :model="model"
                v-if="integrationName === GOHIGHLEVEL_INTEGRATION"/>
        <!-- End Connection Information -->

        <!-- Aloware Calling for HubSpot -->
        <template v-if="integrationName === HUBSPOT_INTEGRATION">
            <hr class="mt-2 mb-3" />
            <hubspot-calling-app-connection-pane />
        </template>
    </div>
</template>

<script>
import ConnectionStatus from './connection-status.vue'
import ConnectionTesterBtn from './connection-tester-btn.vue'
import { ConnectionStatus as ConnectionStatusEnum } from '../../utils/integration-helpers'
import HubspotSettingsWorkflowNotice from '../integrations/hubspot/settings-workflow-notice.vue'
import SettingsAuthentication from "./settings-authentication.vue";
import SalesforceSettingsReauthenticate from "./salesforce/salesforce-settings-reauthenticate.vue";
import HubspotConnectionInfoDetails from "./hubspot/hubspot-connection-info-details.vue";
import SalesforceConnectionInfoDetails from "./salesforce/salesforce-connection-info-details.vue";
import {GOHIGHLEVEL_INTEGRATION, HUBSPOT_INTEGRATION, SALESFORCE_INTEGRATION} from "../../constants/integrations";
import GohighlevelConnectionInfoDetails from "./gohighlevel/connection-info-details.vue";
import HubspotCallingAppConnectionPane from "./hubspot/hubspot-calling-app-connection-pane.vue";

export default {
    components: {
        GohighlevelConnectionInfoDetails,
        ConnectionStatus,
        ConnectionTesterBtn,
        HubspotSettingsWorkflowNotice,
        SettingsAuthentication,
        SalesforceSettingsReauthenticate,
        HubspotConnectionInfoDetails,
        SalesforceConnectionInfoDetails,
        HubspotCallingAppConnectionPane,
    },
    props: {
        model: {
            type: Object,
            required: true
        },
        integrationName: {
            type: String,
            required: true
        },
        integrationNameWithCasing: {
            type: String,
            required: true
        },
        otherCrmIsEnabled: {
            type: Boolean,
            default: false
        },
        hasAccessToken: {
            type: Boolean,
            default: false
        },
        oauth: {
            type: Function,
            required: true
        },
        saveSettings: {
            type: Function,
            required: true
        },
    },
    data: () => ({
        isSaving: false,
        statusToggle: false,
        SALESFORCE_INTEGRATION,
        HUBSPOT_INTEGRATION,
        GOHIGHLEVEL_INTEGRATION,
    }),
    computed: {
        showConnectionError() {
            return this.model.last_connection_error && this.model.connection_status === ConnectionStatusEnum.FAILED
        },
    },
    methods: {
        onClipboardCopy() {
            this.$notify({
                message: 'Error message copied to clipboard.',
                type: 'success'
            })
        },
        onClipboardError(error) {
            console.error('Unable to copy to clipboard', error)
        },
         handleConnectionTest(success) {
            if (success) {
                this.model.connection_status = ConnectionStatusEnum.CONNECTED
                this.$notify({
                    title: 'Integration Connection',
                    message: `Your ${this.integrationNameWithCasing} connection is working!`,
                    type: 'success'
                })
                return
            }

            this.model.connection_status = ConnectionStatusEnum.FAILED
            this.model.last_connection_error = this.model.last_connection_error
                || 'Unable to connect to ' + this.integrationNameWithCasing + '. Please re-authenticate using the button above.'
        },
        async checkAndSave() {
            // if there is one other integration enabled (HubSpot, Pipedrive, Zoho, Guesty, GHL) prevent from saving
            if (this.otherCrmIsEnabled) {
                return false
            }

            if (this.model.status === false) {
                await this.saveSettings()

                window.location.reload()
            }
        },
    },
}
</script>
<style scoped>
/* Status row styling */
.row-container {
    display: flex;
    align-items: center;
    padding: 8px 0;
}

.status-label {
    margin: 0 12px 0 0;
    font-size: 20px;
}

.pane-content {
    display: flex;
    flex-direction: column;
    padding-left: 8px;
    padding-right: 8px;
}

.btn-text {
    font-weight: 600 !important;
}

.connection-error-container {
    display: flex;
    align-items: center;
}

.connection-error-text {
    font-weight: 600;
}

.copy-btn {
    padding: 4px 8px;
    font-size: 12px;
}

/* Copy to clipboard button styling */
.connection-error-container button {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #000000;
    transition: color 0.3s;
}

.connection-error-container button:hover {
    color: #00BF50;
}
</style>
